/* God Panel — Jigyasa platform admin. Owner-only, reached at /god.
   Dark theme, separate auth world from the customer app. */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Crown, Search, LogOut, X, CalendarPlus, Users, Inbox, RefreshCw, IndianRupee, Eye, QrCode, KeyRound, Trash2,
} from "lucide-react";

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
};

const errText = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
};

const godFetch = async (path, opts = {}) => {
  const token = localStorage.getItem("god_token");
  let res;
  try {
    res = await fetch(`${API}/api/god${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  } catch {
    throw new Error("Couldn't reach the server. Please try again.");
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    localStorage.removeItem("god_token");
    window.dispatchEvent(new Event("god:logout"));
  }
  if (!res.ok) throw new Error(data.detail ? errText(data.detail) : `Couldn't reach the server (HTTP ${res.status}). Please try again.`);
  return data;
};

const STATUS_STYLE = {
  trial: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  expired: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  payment_due: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const StatusBadge = ({ status, daysLeft }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[status] || "bg-zinc-700 text-zinc-300 border-zinc-600"}`}>
    {status === "trial" ? `Trial · ${daysLeft}d left`
      : status === "expired" ? "Trial expired"
      : status === "payment_due" ? "Payment due"
      : "Active"}
  </span>
);

const PLAN_OPTIONS = [
  ["solo-monthly", "Solo · monthly (₹495)"],
  ["solo-yearly", "Solo · yearly (₹4,950)"],
  ["team-monthly", "Team · monthly (₹975)"],
  ["team-yearly", "Team · yearly (₹9,750)"],
];

const planLabel = (org) =>
  org.plan || org.intended_plan || "Hasn't picked a plan yet";

const GodLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/god/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(data.detail));
      localStorage.setItem("god_token", data.token);
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900 p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-2">
          <Crown size={22} className="text-amber-400" />
          <h1 className="text-lg font-bold text-white">Jigyasa God Panel</h1>
        </div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">Email</label>
        <input
          data-testid="god-login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
          autoFocus
        />
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">Password</label>
        <input
          data-testid="god-login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-4 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
        />
        {error && <p data-testid="god-login-error" className="mb-3 text-sm text-rose-400">{error}</p>}
        <button
          data-testid="god-login-submit"
          disabled={busy || !email || !password}
          className="w-full rounded-lg bg-amber-500 px-3 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-amber-400 disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3">
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-xs text-zinc-400">{label}</div>
  </div>
);

const FLAG_LABELS = [["demo", "Demo account"], ["test", "Test account"], ["internal", "Internal"]];

const OrgDetail = ({ orgId, onClose, onChanged }) => {
  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);
  const [days, setDays] = useState(14);
  const [endDate, setEndDate] = useState("");
  const [payPlan, setPayPlan] = useState("");
  const [payDate, setPayDate] = useState(todayLocal());
  const [busy, setBusy] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [pinFor, setPinFor] = useState(null);
  const [pinVal, setPinVal] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [pinDoneId, setPinDoneId] = useState(null);

  const setUserPin = async (u) => {
    if (!/^\d{4}$/.test(pinVal)) { setPinErr("The PIN must be exactly 4 digits."); return; }
    setBusy(`pin-${u.id}`); setPinErr("");
    try {
      await godFetch(`/orgs/${orgId}/users/${u.id}/set-pin`, {
        method: "POST",
        body: JSON.stringify({ new_pin: pinVal }),
      });
      setPinFor(null); setPinVal(""); setPinDoneId(u.id);
      setTimeout(() => setPinDoneId(null), 2500);
    } catch (e) {
      setPinErr(e.message);
    }
    setBusy("");
  };

  const load = useCallback(async (attempt = 0) => {
    setError("");
    try {
      const data = await godFetch(`/orgs/${orgId}`);
      setOrg(data);
      setNotes(data.notes || "");
    } catch (e) {
      // The preview backend hot-reloads; a click during a restart gets a
      // gateway error. Retry silently twice before surfacing anything.
      if (attempt < 2) {
        setTimeout(() => load(attempt + 1), 800);
        return;
      }
      setError(e.message);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const extend = () => act("extend", () => godFetch(`/orgs/${orgId}/extend-trial`, { method: "POST", body: JSON.stringify({ days: Number(days) }) }));
  const toggleFlag = (key) => act(key, () => godFetch(`/orgs/${orgId}/flags`, { method: "POST", body: JSON.stringify({ ...org.flags, [key]: !org.flags[key] }) }));
  const saveNotes = () => act("notes", async () => {
    await godFetch(`/orgs/${orgId}/notes`, { method: "POST", body: JSON.stringify({ notes }) });
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  });
  const setExactEnd = () => act("setend", () => godFetch(`/orgs/${orgId}/set-trial-end`, { method: "POST", body: JSON.stringify({ date: endDate }) }));
  const recordPayment = () => act("payment", async () => {
    await godFetch(`/orgs/${orgId}/record-payment`, { method: "POST", body: JSON.stringify({ plan: payPlan, paid_on: payDate }) });
    setPayPlan("");
  });
  const impersonate = async () => {
    setBusy("impersonate");
    setError("");
    try {
      await godFetch(`/orgs/${orgId}/impersonate`, { method: "POST", body: JSON.stringify({}), credentials: "include" });
      window.open("/", "_blank");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const deleteOrg = async () => {
    setBusy("delete");
    setError("");
    try {
      await godFetch(`/orgs/${orgId}/delete`, { method: "POST", body: JSON.stringify({ confirm_name: deleteName }) });
      onChanged();
      onClose();
    } catch (e) {
      setError(e.message);
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div
        data-testid="god-org-detail"
        className="h-full w-full max-w-xl overflow-y-auto border-l border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {!org ? (
          error ? (
            <div className="pt-10 text-center">
              <p data-testid="god-detail-error" className="mb-4 text-sm text-rose-400">{error}</p>
              <button
                data-testid="god-detail-retry"
                onClick={() => load()}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-400"
              >
                Retry
              </button>
            </div>
          ) : (
            <p className="text-zinc-400">Loading…</p>
          )
        ) : (
          <>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{org.name}</h2>
                <p className="text-xs text-zinc-500">Created {fmtDate(org.created_at)} · {org.costume_id || "—"}</p>
              </div>
              <button data-testid="god-detail-close" onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800">
                <X size={18} />
              </button>
            </div>

            {error && <p className="mb-3 text-sm text-rose-400">{error}</p>}

            <div className="mb-5 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
                <div className="text-xs text-zinc-500">Owner</div>
                <div className="text-white">{org.owner_name || "—"}</div>
                <div className="text-zinc-400">{org.owner_phone || "—"}</div>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
                <div className="mb-1 text-xs text-zinc-500">Subscription</div>
                <StatusBadge status={org.status} daysLeft={org.days_left} />
                <div className="mt-1 text-xs text-zinc-400">
                  Plan: {planLabel(org)}
                  {(org.plan || org.intended_plan || "").startsWith("team") && (
                    <span data-testid="god-team-users" className="text-sky-300"> · {org.users_list.length} user{org.users_list.length === 1 ? "" : "s"} created</span>
                  )}
                  {org.paid_until ? (
                    <><br />Paid until: <span data-testid="god-paid-until">{fmtDate(org.paid_until)}</span></>
                  ) : org.trial_ends_at ? (
                    <><br />Trial ends: {fmtDate(org.trial_ends_at)}</>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
                <CalendarPlus size={15} className="text-amber-400" /> Trial controls
              </div>
              <div className="mb-2 flex items-center gap-2">
                <input
                  data-testid="god-extend-days"
                  type="number"
                  min="1"
                  max="365"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="w-20 rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm text-white"
                />
                <span className="text-xs text-zinc-400">days</span>
                <button
                  data-testid="god-extend-btn"
                  onClick={extend}
                  disabled={busy === "extend"}
                  className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-amber-400 disabled:opacity-50"
                >
                  {busy === "extend" ? "Extending…" : "Extend"}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="god-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm text-white"
                />
                <button
                  data-testid="god-set-end-btn"
                  onClick={setExactEnd}
                  disabled={!endDate || busy === "setend"}
                  className="rounded-lg border border-amber-500/50 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
                >
                  {busy === "setend" ? "Setting…" : "Set exact end date"}
                </button>
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-white">
                <IndianRupee size={15} className="text-emerald-400" /> Record payment
              </div>
              <p className="mb-2 text-xs text-zinc-500">
                For UPI / bank-transfer payments until the gateway is live. The subscription runs from the date they paid.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="god-payment-plan"
                  value={payPlan}
                  onChange={(e) => setPayPlan(e.target.value)}
                  className="flex-1 rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm text-white"
                >
                  <option value="">Pick the plan they paid for…</option>
                  {PLAN_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                  Date they paid
                  <input
                    data-testid="god-payment-date"
                    type="date"
                    value={payDate}
                    max={todayLocal()}
                    onChange={(e) => setPayDate(e.target.value)}
                    className="rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm text-white"
                  />
                </label>
                <button
                  data-testid="god-payment-btn"
                  onClick={recordPayment}
                  disabled={!payPlan || !payDate || busy === "payment"}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {busy === "payment" ? "Recording…" : "Record"}
                </button>
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-white">
                <Eye size={15} className="text-sky-400" /> See what they see
              </div>
              <p className="mb-2 text-xs text-zinc-500">
                Opens their Jigyasa in a new tab, read-only — for understanding a problem, never for changing their data. Logged in the audit trail. Lasts 30 minutes.
              </p>
              <button
                data-testid="god-impersonate-btn"
                onClick={impersonate}
                disabled={busy === "impersonate"}
                className="rounded-lg border border-sky-500/50 px-3 py-1.5 text-xs font-semibold text-sky-300 hover:bg-sky-500/10 disabled:opacity-50"
              >
                {busy === "impersonate" ? "Opening…" : "Open read-only view"}
              </button>
            </div>

            <div className="mb-5 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-2 text-sm font-semibold text-white">Flags</div>
              <div className="flex flex-wrap gap-4">
                {FLAG_LABELS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-zinc-300">
                    <input
                      data-testid={`god-flag-${key}`}
                      type="checkbox"
                      checked={!!org.flags[key]}
                      onChange={() => toggleFlag(key)}
                      className="h-4 w-4 accent-amber-500"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-2 text-sm font-semibold text-white">Internal notes</div>
              <textarea
                data-testid="god-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mb-2 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white"
                placeholder="Only you can see this."
              />
              <button
                data-testid="god-notes-save"
                onClick={saveNotes}
                disabled={busy === "notes"}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${notesSaved ? "bg-emerald-600 text-white" : "bg-zinc-700 text-white hover:bg-zinc-600"}`}
              >
                {notesSaved ? "Saved ✓" : "Save notes"}
              </button>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
                <Users size={15} className="text-sky-400" /> Users ({org.users_list.length})
              </div>
              <div className="space-y-1.5">
                {org.users_list.map((u) => (
                  <div key={u.id} data-testid={`god-user-row-${u.phone}`} className="rounded-lg bg-zinc-800/60 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-white">{u.name || "—"}</span>
                        <span className="ml-2 text-xs text-zinc-500">{u.phone}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="rounded-full bg-zinc-700 px-2 py-0.5 text-[11px] text-zinc-300">{u.role}</span>
                        <button
                          data-testid={`god-setpin-toggle-${u.phone}`}
                          onClick={() => {
                            setPinFor(pinFor === u.id ? null : u.id);
                            setPinVal(""); setPinErr("");
                          }}
                          className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold ${
                            pinDoneId === u.id
                              ? "border-emerald-500/50 text-emerald-300"
                              : "border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                          }`}
                        >
                          <KeyRound size={11} /> {pinDoneId === u.id ? "PIN set ✓" : "Set new PIN"}
                        </button>
                      </div>
                    </div>
                    {pinFor === u.id && (
                      <div className="mt-2 border-t border-zinc-700/60 pt-2">
                        <p className="mb-1.5 text-[11px] text-zinc-500">
                          The current PIN can't be shown — it's stored scrambled. Type a fresh
                          4-digit PIN; it works immediately and any login lock is cleared.
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`god-setpin-input-${u.phone}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={pinVal}
                            onChange={(e) => setPinVal(e.target.value.replace(/\D/g, ""))}
                            placeholder="New 4-digit PIN"
                            className="w-36 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-sm text-white tracking-widest placeholder:text-zinc-600 placeholder:tracking-normal focus:border-sky-500 focus:outline-none"
                          />
                          <button
                            data-testid={`god-setpin-confirm-${u.phone}`}
                            onClick={() => setUserPin(u)}
                            disabled={busy === `pin-${u.id}` || pinVal.length !== 4}
                            className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-500 disabled:opacity-40"
                          >
                            {busy === `pin-${u.id}` ? "Setting…" : "Set this PIN"}
                          </button>
                        </div>
                        {pinErr && (
                          <p data-testid={`god-setpin-error-${u.phone}`} className="mt-1.5 text-xs text-rose-400">{pinErr}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-rose-900/60 bg-rose-950/20 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-rose-300">
                <Trash2 size={15} /> Delete this organisation
              </div>
              <p className="mb-2 text-xs text-zinc-500">
                Erases the business completely — every user, enquiry and customer record.
                The owner's phone number is freed to sign up fresh. This cannot be undone.
              </p>
              {!deleteOpen ? (
                <button
                  data-testid="god-delete-open-btn"
                  onClick={() => { setDeleteOpen(true); setDeleteName(""); }}
                  className="rounded-lg border border-rose-500/50 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/10"
                >
                  Delete this organisation…
                </button>
              ) : (
                <div className="space-y-2">
                  <label className="block text-xs text-zinc-400">
                    To confirm, type the business name exactly: <span className="font-semibold text-rose-300">{org.name}</span>
                  </label>
                  <input
                    data-testid="god-delete-confirm-input"
                    value={deleteName}
                    onChange={(e) => setDeleteName(e.target.value)}
                    placeholder={org.name}
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-rose-500"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="god-delete-confirm-btn"
                      onClick={deleteOrg}
                      disabled={deleteName.trim().toLowerCase() !== (org.name || "").trim().toLowerCase() || busy === "delete"}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-40"
                    >
                      {busy === "delete" ? "Deleting…" : "Yes, delete it forever"}
                    </button>
                    <button
                      data-testid="god-delete-cancel-btn"
                      onClick={() => { setDeleteOpen(false); setDeleteName(""); }}
                      className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PAY_FIELDS = [
  ["upi_id", "UPI ID", "e.g. yourname@okhdfcbank"],
  ["bank_name", "Bank name", "e.g. HDFC Bank"],
  ["account_name", "Account holder's name", ""],
  ["account_number", "Account number", ""],
  ["ifsc", "IFSC code", ""],
];

const PaymentSettingsModal = ({ onClose }) => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    godFetch("/payment-settings").then(setForm).catch((e) => setError(e.message));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please pick an image file — a photo or screenshot of your QR code.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setError("That image is too big. Please use one under 1 MB.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => set("upi_qr", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await godFetch("/payment-settings", { method: "POST", body: JSON.stringify(form) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8" onClick={onClose}>
      <div
        data-testid="god-payment-settings-modal"
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2 text-base font-bold text-white">
            <QrCode size={18} className="text-emerald-400" /> Payment settings
          </div>
          <button data-testid="god-payment-settings-close" onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800">
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-xs text-zinc-500">
          What you fill in here is shown to customers on their pay screen when they press "Pay now" — until the online gateway is live.
        </p>

        {!form ? (
          error ? <p className="text-sm text-rose-400">{error}</p> : <p className="text-zinc-400">Loading…</p>
        ) : (
          <>
            <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
              <div className="mb-2 text-sm font-semibold text-white">UPI QR code</div>
              {form.upi_qr ? (
                <div className="mb-2 flex items-start gap-3">
                  <img
                    data-testid="god-payment-qr-preview"
                    src={form.upi_qr}
                    alt="UPI QR code"
                    className="h-36 w-36 rounded-lg border border-zinc-700 bg-white object-contain p-1"
                  />
                  <button
                    data-testid="god-payment-qr-remove"
                    onClick={() => set("upi_qr", "")}
                    className="rounded-lg border border-rose-500/50 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/10"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <p className="mb-2 text-xs text-zinc-500">No QR code uploaded yet.</p>
              )}
              <input
                data-testid="god-payment-qr-file"
                type="file"
                accept="image/*"
                onChange={onFile}
                className="block w-full text-xs text-zinc-400 file:mr-3 file:rounded-lg file:border-0 file:bg-zinc-700 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-zinc-600"
              />
            </div>

            {PAY_FIELDS.map(([key, label, ph]) => (
              <div key={key} className="mb-3">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">{label}</label>
                <input
                  data-testid={`god-payment-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={ph}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
                />
              </div>
            ))}

            <div className="mb-4">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">Note shown to the customer</label>
              <textarea
                data-testid="god-payment-instructions"
                value={form.instructions || ""}
                onChange={(e) => set("instructions", e.target.value)}
                rows={2}
                placeholder="e.g. Send your payment screenshot on WhatsApp and we'll activate you within a few hours."
                className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
              />
            </div>

            {error && <p data-testid="god-payment-error" className="mb-3 text-sm text-rose-400">{error}</p>}
            <button
              data-testid="god-payment-save"
              onClick={save}
              disabled={busy}
              className={`w-full rounded-lg px-3 py-2.5 text-sm font-semibold disabled:opacity-50 ${saved ? "bg-emerald-600 text-white" : "bg-amber-500 text-zinc-950 hover:bg-amber-400"}`}
            >
              {busy ? "Saving…" : saved ? "Saved ✓" : "Save payment details"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setError("");
    if (next.length < 8) {
      setError("The new password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The two new passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await godFetch("/change-password", { method: "POST", body: JSON.stringify({ current_password: current, new_password: next }) });
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div
        data-testid="god-change-password-modal"
        className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-base font-bold text-white">
            <KeyRound size={18} className="text-amber-400" /> Change password
          </div>
          <button data-testid="god-change-password-close" onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800">
            <X size={18} />
          </button>
        </div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">Current password</label>
        <input
          data-testid="god-current-password"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="mb-3 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
          autoFocus
        />
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">New password (at least 8 characters)</label>
        <input
          data-testid="god-new-password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="mb-3 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
        />
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-400">Type the new password again</label>
        <input
          data-testid="god-confirm-password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mb-3 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
        />
        {error && <p data-testid="god-change-password-error" className="mb-3 text-sm text-rose-400">{error}</p>}
        <button
          data-testid="god-change-password-save"
          onClick={save}
          disabled={busy || !current || !next || !confirm || saved}
          className={`w-full rounded-lg px-3 py-2.5 text-sm font-semibold disabled:opacity-50 ${saved ? "bg-emerald-600 text-white" : "bg-amber-500 text-zinc-950 hover:bg-amber-400"}`}
        >
          {busy ? "Changing…" : saved ? "Password changed ✓" : "Change password"}
        </button>
      </div>
    </div>
  );
};

export default function GodPanel() {
  const [me, setMe] = useState(undefined); // undefined=checking, null=logged out
  const [orgs, setOrgs] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaySettings, setShowPaySettings] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  useEffect(() => {
    const onLogout = () => setMe(null);
    window.addEventListener("god:logout", onLogout);
    return () => window.removeEventListener("god:logout", onLogout);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("god_token")) { setMe(null); return; }
    godFetch("/me").then(setMe).catch(() => setMe(null));
  }, []);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await godFetch("/orgs");
      setOrgs(data.organizations);
    } catch { /* 401 handled globally */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (me) loadOrgs(); }, [me, loadOrgs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) =>
      (o.name || "").toLowerCase().includes(q) ||
      (o.owner_phone || "").includes(q) ||
      (o.owner_name || "").toLowerCase().includes(q)
    );
  }, [orgs, query]);

  const stats = useMemo(() => ({
    total: orgs.length,
    trial: orgs.filter((o) => o.status === "trial").length,
    expired: orgs.filter((o) => o.status === "expired").length,
    active: orgs.filter((o) => o.status === "active").length,
  }), [orgs]);

  if (me === undefined) return <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-500">Loading…</div>;
  if (me === null) return <GodLogin onLogin={setMe} />;

  return (
    <div className="min-h-screen bg-zinc-950 pb-16">
      <header className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-950/95 px-6 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-amber-400" />
            <h1 className="text-base font-bold text-white">Jigyasa God Panel</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">{me.email}</span>
            <button
              data-testid="god-payment-settings-btn"
              onClick={() => setShowPaySettings(true)}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-500/50 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10"
            >
              <QrCode size={13} /> Payment settings
            </button>
            <button
              data-testid="god-change-password-btn"
              onClick={() => setShowChangePw(true)}
              className="flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            >
              <KeyRound size={13} /> Change password
            </button>
            <button
              data-testid="god-logout-btn"
              onClick={() => { localStorage.removeItem("god_token"); setMe(null); }}
              className="flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pt-6">
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard testId="god-stat-total" label="Businesses" value={stats.total} />
          <StatCard testId="god-stat-trial" label="On trial" value={stats.trial} />
          <StatCard testId="god-stat-expired" label="Trial expired" value={stats.expired} />
          <StatCard testId="god-stat-active" label="Active" value={stats.active} />
        </div>

        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="god-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by business, owner or phone…"
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 py-2.5 pl-9 pr-3 text-sm text-white outline-none focus:border-amber-500"
            />
          </div>
          <button
            data-testid="god-refresh-btn"
            onClick={loadOrgs}
            className="flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-2.5 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-900 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Original start</th>
                <th className="px-4 py-3">Current start</th>
                <th className="px-4 py-3">Ends / paid until</th>
                <th className="px-4 py-3 text-right"><Users size={13} className="inline" /></th>
                <th className="px-4 py-3 text-right"><Inbox size={13} className="inline" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70 bg-zinc-950">
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`god-org-row-${o.id}`}
                  onClick={() => setSelected(o.id)}
                  className="cursor-pointer transition-colors hover:bg-zinc-900"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{o.name}</div>
                    <div className="flex gap-1 text-[10px] text-zinc-500">
                      {Object.entries(o.flags).filter(([, v]) => v).map(([k]) => (
                        <span key={k} className="rounded bg-zinc-800 px-1.5 py-0.5 uppercase">{k}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">
                    {o.owner_name || "—"}
                    <div className="text-xs text-zinc-500">{o.owner_phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} daysLeft={o.days_left} /></td>
                  <td data-testid={`god-org-original-start-${o.id}`} className="px-4 py-3 text-zinc-400">{fmtDate(o.original_start)}</td>
                  <td data-testid={`god-org-current-start-${o.id}`} className="px-4 py-3 text-zinc-400">{fmtDate(o.current_start)}</td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(o.paid_until || o.trial_ends_at)}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{o.users}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{o.enquiries}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">No businesses match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {selected && <OrgDetail orgId={selected} onClose={() => setSelected(null)} onChanged={loadOrgs} />}
      {showPaySettings && <PaymentSettingsModal onClose={() => setShowPaySettings(false)} />}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
